import React, {
    FC,
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useSearchParams } from 'react-router'
import { useEvent } from 'react-use-event-hook'
import { getPostedMessageType, trackPostedMessage } from '@components/Analytics/postedMessage'
import { getCachedSpaceDataForAnalytics } from '@components/Analytics/useGatherSpaceDetailsAnalytics'
import { MediaDropContextProvider } from '@components/MediaDropContext/MediaDropContext'
import { RegisterEditFunction } from '@components/MessageTimeline/components/RegisterEditFunction'
import { MessageTimeline } from '@components/MessageTimeline/MessageTimeline'
import { MessageTimelineWrapper } from '@components/MessageTimeline/MessageTimelineWrapper'
import { ReplyContextProvider } from '@components/ReplyToMessageContext/ReplyToMessageProvider'
import { TownsEditorContainer } from '@components/RichTextPlate/TownsEditorContainer'
import { useTradeAnalytics } from '@components/Web3/Trading/hooks/useTradeAnalytics'
import { useSendTradeTransaction } from '@components/Web3/Trading/hooks/useTradeQuote'
import { TradeComponent } from '@components/Web3/Trading/TradeComponent'
import { useTradeSettings } from '@components/Web3/Trading/tradeSettingsStore'
import { TradingBalanceAndSlippage } from '@components/Web3/Trading/TradingBalanceAndSlippage'
import {
    EvmTransactionRequest,
    SolanaTransactionRequest,
} from '@components/Web3/Trading/TradingContextProvider'
import { TransactionTooltip } from '@components/Web3/Trading/TransactionTooltip'
import { QuoteMetaData, QuoteStatus } from '@components/Web3/Trading/types'
import {
    isDMChannelStreamId,
    isGDMChannelStreamId,
    ThreadStatsData,
    TickerAttachment,
    TimelineEvent,
} from '@towns-protocol/sdk'
import { Box, BoxProps, FancyButton, Stack } from '@ui'
import { useDevice } from 'hooks/useDevice'
import { useIsChannelReactable } from 'hooks/useIsChannelReactable'
import { useIsChannelWritable } from 'hooks/useIsChannelWritable'
import { useSendReply } from 'hooks/useSendReply'
import { useSpaceChannels } from 'hooks/useSpaceChannels'
import { isEqual } from 'lodash-es'
import { WalletReady } from 'privy/WalletReady'
import {
    SendMessageOptions,
    TSigner,
    useConnectivity,
    useMyProfile,
    useSpaceMembers,
} from 'use-towns-client'

import { getTickerAttachment } from './getTickerAttachment'
import { TickerThreadContext } from './TickerThreadContext'

export const MessageThread = (props: {
    threadData: ThreadStatsData
    parentMessage: TimelineEvent | undefined
    messages: TimelineEvent[]
    spaceId: string | undefined
    channelId: string
    MessageContainer?: FC<PropsWithChildren>
    EditorContainer?: FC<PropsWithChildren>
    timelineProps: React.ComponentProps<typeof MessageTimeline>
    editorProps: Partial<React.ComponentProps<typeof ThreadEditor>>
}) => {
    const {
        threadData,
        parentMessage,
        messages,
        spaceId,
        channelId,
        MessageContainer,
        EditorContainer,
        timelineProps,
    } = props

    const messageId = parentMessage?.eventId

    const messagesWithParent = useMemo(() => {
        return parentMessage ? [parentMessage, ...messages] : messages
    }, [messages, parentMessage])

    const { loggedInWalletAddress } = useConnectivity()

    const { isChannelWritable: isChannelWritableSource } = useIsChannelWritable(
        spaceId,
        channelId,
        loggedInWalletAddress,
    )
    // default isChannelWritable to true while loading for threads because you're already participating
    const isChannelWritable = isChannelWritableSource !== undefined ? isChannelWritableSource : true
    const isDmOrGDM = isDMChannelStreamId(channelId) || isGDMChannelStreamId(channelId)
    const { isChannelReactable: isChannelReactableSource } = useIsChannelReactable(
        isDmOrGDM ? undefined : spaceId,
        channelId,
        loggedInWalletAddress,
    )
    // default isChannelReactable to true while loading for threads because you're already participating
    const isChannelReactable =
        isChannelReactableSource !== undefined ? isChannelReactableSource : true

    const imageUploadTitle = isChannelWritable
        ? `Upload to thread`
        : isChannelWritable === false
          ? `You don't have permission to send media to this channel`
          : `Loading permissions`

    const tickerAttachment = useMemo(() => {
        return getTickerAttachment(parentMessage)
    }, [parentMessage])

    const userId = useMyProfile()?.userId
    const lastMessageEditRef = useRef<((messageId: string) => void) | null>(null)

    const registerEditFunction = useCallback((editFn: (userId: string) => void) => {
        lastMessageEditRef.current = editFn
    }, [])

    const handleArrowEscape = useCallback(
        (direction: 'up' | 'down') => {
            if (direction === 'up' && lastMessageEditRef.current && userId) {
                lastMessageEditRef.current(userId)
            }
        },
        [userId],
    )

    const editorProps = {
        spaceId,
        channelId,
        messageId,
        tickerAttachment,
        threadData,
        isChannelWritable,
        onArrowEscape: handleArrowEscape,
    }

    const timelineContent = (
        <MessageTimelineWrapper
            spaceId={spaceId}
            channelId={channelId}
            threadParentId={messageId}
            events={messagesWithParent}
            isChannelWritable={isChannelWritable}
            isChannelReactable={isChannelReactable}
        >
            <MessageTimeline {...timelineProps} />
            <RegisterEditFunction onRegister={registerEditFunction} />
        </MessageTimelineWrapper>
    )

    const editorContent = tickerAttachment ? (
        <WalletReady>
            {({ getSigner }) => (
                <ThreadEditor {...editorProps} {...props.editorProps} getSigner={getSigner} />
            )}
        </WalletReady>
    ) : (
        <ThreadEditor {...editorProps} {...props.editorProps} />
    )

    return (
        <ReplyContextProvider canReplyInline canReplyInThread={false}>
            <TickerThreadContext.Provider value={tickerAttachment}>
                <MediaDropContextProvider
                    title={imageUploadTitle}
                    channelId={channelId}
                    spaceId={spaceId}
                    eventId={messageId}
                    key={messageId}
                    disableDrop={!isChannelWritable}
                >
                    {MessageContainer ? (
                        <MessageContainer>{timelineContent}</MessageContainer>
                    ) : (
                        timelineContent
                    )}
                    {EditorContainer ? (
                        <EditorContainer>{editorContent}</EditorContainer>
                    ) : (
                        editorContent
                    )}
                </MediaDropContextProvider>
            </TickerThreadContext.Provider>
        </ReplyContextProvider>
    )
}

const ThreadEditor = (props: {
    spaceId?: string
    channelId: string
    messageId?: string
    tickerAttachment?: TickerAttachment
    threadData?: ThreadStatsData
    isChannelWritable?: boolean
    getSigner?: () => Promise<TSigner | undefined>
    autoFocus?: boolean
    displayButtons?: 'always' | 'on-focus'
    background?: BoxProps['background']
    onArrowEscape?: (direction: 'up' | 'down') => void
}) => {
    const {
        channelId,
        getSigner,
        isChannelWritable,
        messageId,
        threadData,
        spaceId,
        tickerAttachment,
        autoFocus,
        displayButtons,
        background,
        onArrowEscape,
    } = props
    const { isTouch } = useDevice()
    const userId = useMyProfile()?.userId
    const channels = useSpaceChannels()
    const { memberIds } = useSpaceMembers()

    const [tradeData, setTradeData] = useState<
        | {
              request: EvmTransactionRequest | SolanaTransactionRequest
              metaData: QuoteMetaData
          }
        | undefined
    >(undefined)

    const { sendTradeTransaction: onSendTrade } = useSendTradeTransaction({
        request: tradeData?.request,
        chainId: tickerAttachment?.chainId,
        skipPendingToast: true,
    })

    const { sendReply } = useSendReply(messageId)

    const allowEmptyMessage = !!tickerAttachment

    const resetRef = useRef<{ reset: () => void }>({ reset: () => {} })

    const [isTradingProgress, setIsTradingProgress] = useState(false)

    const { trackTradeSubmitted, trackTradeSuccess, trackTradeFailed } = useTradeAnalytics({
        chainId: tickerAttachment?.chainId ?? '',
        address: tickerAttachment?.address ?? '',
    })

    const onSend = useCallback(
        async (value: string, options: SendMessageOptions | undefined) => {
            if (tradeData && onSendTrade && getSigner) {
                const emptyMessage = options && 'emptyMessage' in options && !!options?.emptyMessage
                const tradeTokenAnalytics = {
                    entryPoint: 'channel_thread' as const,
                    messageAdded: !emptyMessage,
                    tradeAction: tradeData.metaData.mode,
                    tradeAmount: tradeData.metaData.analytics.amount,
                    tradeValueUSD: tradeData.metaData.analytics.amountUSD,
                }
                trackTradeSubmitted(tradeTokenAnalytics)

                setIsTradingProgress(true)
                try {
                    await onSendTrade?.(getSigner)
                    trackTradeSuccess(tradeTokenAnalytics)
                } catch (error) {
                    trackTradeFailed({
                        ...tradeTokenAnalytics,
                        reason: error instanceof Error ? error.message : 'Unknown error',
                    })
                } finally {
                    resetRef.current?.reset()
                    setIsTradingProgress(false)
                }
                if (emptyMessage) {
                    return
                }
            }

            trackPostedMessage({
                spaceId,
                channelId,
                messageType: getPostedMessageType(value, {
                    messageType: options?.messageType,
                }),
                threadId: 'threadId',
                canReplyInline: undefined,
                replyToEventId: undefined,
                ...getCachedSpaceDataForAnalytics(),
            })
            const userIds = threadData?.userIds ?? new Set<string>()
            if (threadData?.parentEvent) {
                userIds.add(threadData.parentEvent.sender.id)
            }
            sendReply(value, channelId, options)
        },
        [
            channelId,
            getSigner,
            onSendTrade,
            sendReply,
            spaceId,
            threadData?.parentEvent,
            threadData?.userIds,
            trackTradeFailed,
            trackTradeSubmitted,
            trackTradeSuccess,
            tradeData,
        ],
    )

    const [quoteStatus, setQuoteStatus] = useState<QuoteStatus | undefined>(undefined)

    const tradeDataRef = useRef(tradeData)
    tradeDataRef.current = tradeData

    const onQuoteStatusChanged = useEvent((status: QuoteStatus | undefined) => {
        setQuoteStatus(status)
        if (!status) {
            setTradeData(undefined)
            return
        }

        if (status.status === 'ready') {
            if (
                isEqual(tradeDataRef.current?.request, status.data.request) &&
                isEqual(tradeDataRef.current?.metaData, status.data.metaData)
            ) {
                return
            }
            setTradeData(status.data)
        } else {
            setTradeData(undefined)
        }
    })

    const slippage = useTradeSettings((state) => state.slippage)

    const [params, setParams] = useSearchParams()
    const [mode, setMode] = useState<'buy' | 'sell'>(() => {
        const mode = params.get('mode')
        return mode === 'sell' ? 'sell' : 'buy'
    })
    useEffect(() => {
        setMode(() => {
            const mode = params.get('mode')

            if (mode === 'buy' || mode === 'sell') {
                return mode
            }
            return 'buy'
        })
    }, [params])

    const onModeChanged = useEvent((mode: 'buy' | 'sell') => {
        setParams((prev) => {
            prev.set('mode', mode)
            return prev
        })
    })

    const renderSendButton = useCallback(
        (onSend: () => void) => {
            return getSigner &&
                quoteStatus &&
                quoteStatus?.status !== 'error' &&
                typeof mode === 'string' ? (
                <Stack horizontal gap="sm" alignItems="center">
                    <Box
                        centerContent
                        tooltip={
                            !tradeData?.metaData?.value.value ? (
                                ``
                            ) : (
                                <TransactionTooltip
                                    mode={mode}
                                    chainId={tickerAttachment?.chainId ?? ''}
                                    tradeData={tradeData?.metaData}
                                    slippage={slippage}
                                />
                            )
                        }
                        opacity={
                            quoteStatus?.status !== 'ready' || isTradingProgress ? '0.5' : undefined
                        }
                    >
                        <FancyButton
                            compact="x4"
                            gap="xxs"
                            paddingLeft="sm"
                            paddingRight="md"
                            background={mode === 'buy' ? 'positive' : 'peach'}
                            borderRadius="full"
                            icon="lightning"
                            spinner={quoteStatus?.status === 'loading' || isTradingProgress}
                            disabled={quoteStatus?.status !== 'ready' || isTradingProgress}
                            onClick={onSend}
                        >
                            {mode === 'buy' ? 'Buy' : 'Sell'}
                        </FancyButton>
                    </Box>
                </Stack>
            ) : undefined
        },
        [
            getSigner,
            isTradingProgress,
            mode,
            quoteStatus,
            slippage,
            tickerAttachment?.chainId,
            tradeData?.metaData,
        ],
    )

    const renderTradingBottomBar = useMemo(
        () => (
            <TradingBalanceAndSlippage
                tokenAddress={tickerAttachment?.address}
                mode={mode}
                chainId={tickerAttachment?.chainId}
            />
        ),
        [mode, tickerAttachment],
    )

    const placeholder = tradeData?.request ? 'Add an optional message...' : 'Reply...'

    const editor = (
        <TownsEditorContainer
            isFullWidthOnTouch
            background={tickerAttachment ? 'inherit' : background}
            key={`${messageId}-${isChannelWritable ? '' : '-readonly'}`}
            editable={!!isChannelWritable}
            displayButtons={displayButtons ?? 'on-focus'}
            placeholder={placeholder}
            storageId={`${channelId}-${messageId}`}
            threadId={messageId}
            channels={channels}
            spaceMemberIds={memberIds}
            userId={userId}
            allowEmptyMessage={allowEmptyMessage}
            renderSendButton={renderSendButton}
            renderTradingBottomBar={renderTradingBottomBar}
            autoFocus={autoFocus ?? !isTouch}
            threadPreview={threadData?.parentEvent?.fallbackContent}
            onSend={onSend}
            onArrowEscape={onArrowEscape}
        />
    )

    const threadInfo = useMemo(() => {
        return messageId ? { channelId, messageId } : undefined
    }, [channelId, messageId])

    return (
        <>
            {tickerAttachment ? (
                <Box elevate rounded={{ touch: 'none', default: 'md' }}>
                    <Box padding="md" paddingBottom="xs">
                        <TradeComponent
                            mode={mode}
                            tokenAddress={tickerAttachment.address}
                            chainId={tickerAttachment.chainId}
                            threadInfo={threadInfo}
                            resetRef={resetRef}
                            onQuoteStatusChanged={onQuoteStatusChanged}
                            onModeChanged={onModeChanged}
                        />
                    </Box>
                    <Box>{editor}</Box>
                </Box>
            ) : (
                editor
            )}
        </>
    )
}
